import styled from '@emotion/styled'
import { ApiProductOptionGuide } from '@storefronts/common-types'
import type { FC } from 'react'
import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'
import { PRIMARY_PAGE_TEXT_COLOR_VAR } from '../../../context/CustomizationContextProvider'
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock'
import { imageUrlTransform } from '../../../utils/imageUrlTransform'
import { scaledFontSize } from '../../../style/scrollingLayout'
import { InspectOverlayHost } from '../../shared/InspectOverlayHost'

const TriggerButton = styled.button({
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: scaledFontSize(13),
    color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    font: 'inherit',
    textAlign: 'inherit',
})

const Backdrop = styled(InspectOverlayHost)({
    position: 'fixed',
    inset: 0,
    zIndex: 99999,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
})

const Inner = styled.div({
    position: 'relative',
    maxWidth: '90vw',
    maxHeight: '90vh',
})

const LightboxImage = styled.img({
    maxWidth: '90vw',
    maxHeight: '90vh',
    width: 'auto',
    height: 'auto',
    objectFit: 'contain',
    display: 'block',
})

const CloseButton = styled.button({
    position: 'fixed',
    top: 24,
    right: 24,
    background: 'rgba(255, 255, 255, 0.12)',
    border: 'none',
    borderRadius: '50%',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: scaledFontSize(22),
    lineHeight: 1,
    cursor: 'pointer',
    zIndex: 100001,
    '&:hover': {
        background: 'rgba(255, 255, 255, 0.22)',
    },
})

function GuideLightboxPortal({
    assetUrl,
    label,
    onClose,
}: {
    assetUrl: string
    label: string
    onClose: () => void
}) {
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose()
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [onClose])

    useBodyScrollLock(true)

    return createPortal(
        <Backdrop role="presentation" onClick={onClose}>
            <CloseButton
                type="button"
                onClick={(e) => {
                    e.stopPropagation()
                    onClose()
                }}
                aria-label="Close"
            >
                &times;
            </CloseButton>
            <Inner
                role="dialog"
                aria-modal="true"
                aria-label={label}
                onClick={(e) => e.stopPropagation()}
            >
                <LightboxImage src={assetUrl} alt={label} />
            </Inner>
        </Backdrop>,
        document.body,
    )
}

export const ProductOptionGuideRow: FC<{
    guide: ApiProductOptionGuide
}> = ({ guide }) => {
    const imageUrl = guide.imageUrl
    const [open, setOpen] = useState(false)

    if (imageUrl == null || imageUrl === '') {
        return null
    }

    const assetUrl = imageUrlTransform(imageUrl)

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                marginBottom: 10,
            }}
        >
            <TriggerButton
                type="button"
                onClick={() => setOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                {guide.label}
            </TriggerButton>
            {open ? (
                <GuideLightboxPortal
                    assetUrl={assetUrl}
                    label={guide.label}
                    onClose={() => setOpen(false)}
                />
            ) : null}
        </div>
    )
}
